import {Injectable, OnDestroy, signal} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Capacitor} from '@capacitor/core';
import {Preferences} from '@capacitor/preferences';
import {
  ConsentStatus,
  ConsentType,
  FirebaseAnalytics,
} from '@capacitor-firebase/analytics';
import {filter, Subscription} from 'rxjs';
import {AnalyticsConsent} from './analytics-consent.plugin';
import {isStagingEnvironment} from '../../auth/native-auth-config';

const ANALYTICS_CONSENT_CHOICE_KEY = 'analytics-consent-choice';
type AnalyticsConsentChoice = 'granted' | 'denied';

export const analyticsConsentClient = {
  requestConsentStatus: () => AnalyticsConsent.requestConsentStatus(),
};

export const analyticsPreferencesClient = {
  getConsentChoice: () => Preferences.get({key: ANALYTICS_CONSENT_CHOICE_KEY}),
  setConsentChoice: (choice: AnalyticsConsentChoice) => Preferences.set({
    key: ANALYTICS_CONSENT_CHOICE_KEY,
    value: choice,
  }),
};

export const firebaseAnalyticsClient = {
  logEvent: (options: Parameters<typeof FirebaseAnalytics.logEvent>[0]) => FirebaseAnalytics.logEvent(options),
  resetAnalyticsData: () => FirebaseAnalytics.resetAnalyticsData(),
  setConsent: (options: Parameters<typeof FirebaseAnalytics.setConsent>[0]) => FirebaseAnalytics.setConsent(options),
  setCurrentScreen: (options: Parameters<typeof FirebaseAnalytics.setCurrentScreen>[0]) => FirebaseAnalytics.setCurrentScreen(options),
  setEnabled: (enabled: boolean) => FirebaseAnalytics.setEnabled({enabled}),
};

const LANGUAGE_SEGMENTS = new Set(['en', 'de', 'uk', 'ru']);
const PRIVATE_VALUE_PATTERN = /^(?:\d+|[0-9a-f]{8}-[0-9a-f-]{27,}|[a-z0-9_-]{20,})$/i;
const PRIVATE_ROUTE_PARENTS = new Set(['containers', 'items', 'profiles', 'publication-requests', 'users']);
const STATIC_ROUTE_VALUES = new Set(['create', 'new']);

export type AnalyticsLoginMethod = 'password' | 'google';
export type AnalyticsEntityType = 'container' | 'item';
export type AnalyticsSubscriptionOutcome = 'started' | 'succeeded' | 'failed' | 'cancelled';

export function normalizeAnalyticsScreenName(url: string): string {
  const path = url.split(/[?#]/, 1)[0] ?? '';
  const rawSegments = path.split('/').filter(Boolean);
  const segments = LANGUAGE_SEGMENTS.has(rawSegments[0]?.toLowerCase())
    ? rawSegments.slice(1)
    : rawSegments;

  const safeSegments = segments.map((segment, index) => {
    const normalizedSegment = segment.toLowerCase();
    const parentSegment = segments[index - 1]?.toLowerCase();
    const isPrivateRouteSlot = PRIVATE_ROUTE_PARENTS.has(parentSegment)
      && !STATIC_ROUTE_VALUES.has(normalizedSegment);
    const isPublicItemSlug = segments[0]?.toLowerCase() === 'items' && index === 2;

    return PRIVATE_VALUE_PATTERN.test(segment) || isPrivateRouteSlot || isPublicItemSlug
      ? 'id'
      : normalizedSegment;
  });
  return safeSegments.length > 0 ? safeSegments.join('_').slice(0, 100) : 'landing';
}

@Injectable({providedIn: 'root'})
export class AnalyticsService implements OnDestroy {
  logFavorite(name: 'favorites_widget_view' | 'favorite_added' | 'favorite_removed' | 'favorite_opened' | 'favorites_widget_hidden', params: Record<string, string>): Promise<void> {
    return this.logEvent(name, params);
  }
  private navigationSubscription?: Subscription;
  private initializationPromise?: Promise<void>;
  private collectionAllowed = false;
  private readonly consentPromptVisible = signal(false);
  private lastScreenName = '';

  constructor(private readonly router: Router) {}

  get isAndroid(): boolean {
    return !isStagingEnvironment() && Capacitor.getPlatform() === 'android';
  }

  get requiresConsentDecision(): boolean {
    return this.isAndroid && this.consentPromptVisible();
  }

  async initialize(): Promise<void> {
    if (!this.isAndroid) {
      return;
    }

    this.initializationPromise ??= this.initializeAndroid();
    await this.initializationPromise;
  }

  async isCollectionEnabled(): Promise<boolean> {
    await this.initialize();
    return this.collectionAllowed;
  }

  private async initializeAndroid(): Promise<void> {
    try {
      await this.denyAdvertisingConsent();
      await this.setAnalyticsConsent(false);

      const choice = await this.readConsentChoice();
      if (choice !== null) {
        await this.setAnalyticsConsent(choice === 'granted');
        this.startScreenTracking();
        return;
      }

      const result = await analyticsConsentClient.requestConsentStatus();
      if (result.status === 'notRequired') {
        await this.setAnalyticsConsent(true);
        this.startScreenTracking();
        return;
      }

      await this.setAnalyticsConsent(false);
      this.consentPromptVisible.set(result.status === 'required' || result.status === 'obtained');
    } catch {
      await this.setAnalyticsConsent(false);
    }
  }

  async isOptedOut(): Promise<boolean> {
    if (!this.isAndroid) {
      return false;
    }

    return (await this.readConsentChoice()) === 'denied';
  }

  async setOptedOut(optedOut: boolean): Promise<boolean> {
    if (!this.isAndroid) {
      return false;
    }

    await this.initialize();
    const choice: AnalyticsConsentChoice = optedOut ? 'denied' : 'granted';
    await analyticsPreferencesClient.setConsentChoice(choice);
    this.consentPromptVisible.set(false);
    if (optedOut) {
      await this.setAnalyticsConsent(false);
      await firebaseAnalyticsClient.resetAnalyticsData();
      return false;
    }

    await this.setAnalyticsConsent(true);
    this.startScreenTracking();
    return this.collectionAllowed;
  }

  async completeConsentDecision(granted: boolean): Promise<boolean> {
    if (!this.requiresConsentDecision) {
      return this.collectionAllowed;
    }

    await analyticsPreferencesClient.setConsentChoice(granted ? 'granted' : 'denied');
    this.consentPromptVisible.set(false);
    await this.setAnalyticsConsent(granted);
    if (this.collectionAllowed) {
      this.startScreenTracking();
    }
    return this.collectionAllowed;
  }

  login(method: AnalyticsLoginMethod): Promise<void> {
    return this.logEvent('login', {method});
  }

  signUp(method: AnalyticsLoginMethod): Promise<void> {
    return this.logEvent('sign_up', {method});
  }

  entityCreated(entityType: AnalyticsEntityType): Promise<void> {
    return this.logEvent(`${entityType}_created`);
  }

  searchCompleted(scope: 'global' | 'items' | 'containers'): Promise<void> {
    return this.logEvent('search', {scope});
  }

  qrScanCompleted(): Promise<void> {
    return this.logEvent('qr_scan_completed');
  }

  subscriptionOutcome(outcome: AnalyticsSubscriptionOutcome): Promise<void> {
    return this.logEvent('subscription_checkout', {outcome});
  }

  ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();
  }

  private async denyAdvertisingConsent(): Promise<void> {
    await Promise.all(
      [ConsentType.AdStorage, ConsentType.AdUserData, ConsentType.AdPersonalization]
        .map((type) => firebaseAnalyticsClient.setConsent({type, status: ConsentStatus.Denied}).catch(() => undefined)),
    );
  }

  private async readConsentChoice(): Promise<AnalyticsConsentChoice | null> {
    const {value} = await analyticsPreferencesClient.getConsentChoice();
    return value === 'granted' || value === 'denied' ? value : null;
  }

  private async setAnalyticsConsent(granted: boolean): Promise<void> {
    if (!granted) {
      this.collectionAllowed = false;
      this.navigationSubscription?.unsubscribe();
      this.navigationSubscription = undefined;
      this.lastScreenName = '';
      await Promise.all([
        firebaseAnalyticsClient.setConsent({
          type: ConsentType.AnalyticsStorage,
          status: ConsentStatus.Denied,
        }).catch(() => undefined),
        firebaseAnalyticsClient.setEnabled(false).catch(() => undefined),
      ]);
      return;
    }

    try {
      await firebaseAnalyticsClient.setConsent({
        type: ConsentType.AnalyticsStorage,
        status: ConsentStatus.Granted,
      });
      await firebaseAnalyticsClient.setEnabled(true);
      this.collectionAllowed = true;
    } catch {
      await this.setAnalyticsConsent(false);
    }
  }

  private startScreenTracking(): void {
    if (!this.collectionAllowed || this.navigationSubscription) {
      return;
    }

    void this.trackScreen(this.router.url);
    this.navigationSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => void this.trackScreen(event.urlAfterRedirects));
  }

  private async trackScreen(url: string): Promise<void> {
    const screenName = normalizeAnalyticsScreenName(url);
    if (!this.collectionAllowed || screenName === this.lastScreenName) {
      return;
    }

    this.lastScreenName = screenName;
    try {
      await firebaseAnalyticsClient.setCurrentScreen({screenName});
    } catch {
      // Analytics must never interrupt navigation.
    }
  }

  private async logEvent(name: string, params?: Record<string, string>): Promise<void> {
    if (!this.isAndroid || !this.collectionAllowed) {
      return;
    }

    try {
      await firebaseAnalyticsClient.logEvent({name, params});
    } catch {
      // Analytics must never interrupt the user action being measured.
    }
  }
}
